import moment from "moment";

// ค่าตรงกับ API (yamaha_backend/configs/seatBooking.js)
export const BOOKING_STATUS = {
  DRAFT: "1",
  PENDING: "3",
  APPROVED: "4",
  REJECTED: "5",
  CANCELLED_BY_ADMIN: "8",
};

// labelKey แปลด้วย t(labelKey, { ns: "seat" })
export const RENDER_SEAT_BOOKING_STATUS = (status) => {
  switch (status) {
    case BOOKING_STATUS.PENDING:
      return { labelKey: "Waiting for admin to verify the slip", bgColor: "#4B42A8", txtColor: "#FFFFFF" };
    case BOOKING_STATUS.APPROVED:
      return { labelKey: "Booking confirmed", bgColor: "#34C759", txtColor: "#FFFFFF" };
    case BOOKING_STATUS.REJECTED:
      return { labelKey: "Rejected", bgColor: "#FF0031", txtColor: "#FFFFFF" };
    case BOOKING_STATUS.CANCELLED_BY_ADMIN:
      return { labelKey: "Cancelled by admin", bgColor: "#848484", txtColor: "#FFFFFF" };
    default:
      return { labelKey: "-", bgColor: "#EDEDED", txtColor: "#000000" };
  }
};

// วันเวลาแบบ design เช่น 5 พ.ย. 2569 20:12 (ภาษาอังกฤษ 5 Nov 2026 20:12)
const MONTH_SHORT_TH = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const MONTH_SHORT_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const SEAT_BOOKING_DATE_TEXT = (value, language) => {
  if (!value) return "-";
  const d = moment(value);
  const isTh = language !== "en";
  const month = (isTh ? MONTH_SHORT_TH : MONTH_SHORT_EN)[d.month()];
  return `${d.date()} ${month} ${d.year() + (isTh ? 543 : 0)} ${d.format("HH:mm")}`;
};

// state จาก API แผนที่นั่งลูกค้า: free | mine | pending | taken
// สีตาม design S2: ที่ไม่ว่างใช้ตัวเลขสีเดียวกับพื้น ไม่ให้ตาลายตอนดูที่ว่าง
export const SEAT_STATE_STYLE = {
  free: { bg: "#C2B0D6", color: "#331A59", labelKey: "Available" },
  mine: { bg: "#4B1E78", color: "#FFFFFF", labelKey: "Selected" },
  pending: { bg: "#F2A33B", color: "#FFFFFF", labelKey: "Pending confirmation" },
  taken: { bg: "#5C5C5C", color: "#5C5C5C", labelKey: "Unavailable" },
};

// สีป้ายโซนในหัวผังโซน วนตามลำดับโซน (ชุดสีเดียวกับผังฮอลล์ใน design)
const ZONE_BADGE_COLORS = ["#2A9D8F", "#7B4FB5", "#4A86D9", "#E08A3C", "#5FA35A"];
export const ZONE_BADGE_COLOR = (index) => ZONE_BADGE_COLORS[Math.max(0, index) % ZONE_BADGE_COLORS.length];

// error code จาก API -> key ข้อความ (ns: "seat")
const ERROR_KEY_BY_CODE = {
  SEAT_UNAVAILABLE: "Some seats were just booked by someone else. Please choose again.",
  DRAFT_EXPIRED: "Your booking time has expired. The seats have been released.",
  NOT_DRAFT: "Your booking time has expired. The seats have been released.",
  DRAFT_EXISTS: "You have a booking in progress. Please complete it first.",
  QUOTA_FULL: "Online seats are sold out",
  BOOKING_NOT_READY: "Booking is not open yet",
  REGISTRATION_NOT_STARTED: "Booking is not open yet",
  REGISTRATION_CLOSED: "Booking is closed",
  ACTIVITY_INACTIVE: "Booking is closed",
  MEMBER_TYPE_NOT_ALLOWED: "Seat booking is available for individual members only. Please switch to your personal account.",
  SEAT_LIMIT: "You have selected more seats than allowed",
  SLIP_REQUIRED: "Please attach the transfer slip",
  UPLOAD_INVALID: "The slip must be a JPG or PNG image no larger than 8 MB",
  SLIP_IMAGE_ONLY: "The slip must be a JPG or PNG image no larger than 8 MB",
  SLIP_DUPLICATE: "This slip has already been used. Please attach a new slip.",
  SLIP_INVALID: "This image is not a transfer slip. Please attach the slip from your banking app.",
  BOOKER_REQUIRED: "Please fill in the booker details",
  DETAILS_REQUIRED: "Please fill in the booker details",
  EMAIL_INVALID: "Invalid email",
  TAX_REQUIRED: "Please fill in the tax invoice name and address",
  TAX_ID_INVALID: "Tax ID must be 13 digits",
  SCHOOL_INVALID: "Please select the school again",
  NOT_PENDING: "This booking has already been reviewed",
  SUBMIT_CONFLICT: "Something went wrong. Please try again.",
  DRAFT_CHANGED: "Something went wrong. Please try again.",
  SLIP_LIMIT: "You have reached the maximum number of slips for this booking",
  // สแกนตั๋วหน้างาน (เจ้าหน้าที่)
  TICKET_INVALID: "Invalid ticket",
  TICKET_NOT_VALID: "This ticket is no longer valid",
  NOT_EVENT_DAY: "Tickets can be scanned on the event day only",
  TICKET_USED: "This ticket was already used",
};

// API ตอบ error เป็น { error: { code, message } } ผ่าน res.data (axios response) ต้องอ่านจาก res.data.error เท่านั้น
export const SEAT_BOOKING_ERROR_KEY = (res, fallback = "Something went wrong. Please try again.") => {
  const code = res && res.data && res.data.error && res.data.error.code;
  return ERROR_KEY_BY_CODE[code] || fallback;
};

// ข้อความเมื่อยังจองไม่ได้ (จาก closed_reason ของ API) ยังไม่ถึงช่วงลงทะเบียนบอกวันเวลาเปิด
export const SEAT_BOOKING_CLOSED_TEXT = (t, info) => {
  if (info && info.closed_reason === "REGISTRATION_NOT_STARTED" && info.registration_opens_at) {
    return t("Booking opens on {{date}}", {
      ns: "seat",
      date: new Date(info.registration_opens_at).toLocaleString("th-TH", {
        timeZone: "Asia/Bangkok",
        dateStyle: "medium",
        timeStyle: "short",
      }),
    });
  }
  if (info && ["REGISTRATION_NOT_STARTED", "BOOKING_NOT_READY"].includes(info.closed_reason)) {
    return t("Booking is not open yet", { ns: "seat" });
  }
  return t("Booking is closed", { ns: "seat" });
};

export const SEAT_BOOKING_ERROR_CODE = (res) =>
  (res && res.data && res.data.error && res.data.error.code) || null;

